import { memo, useMemo } from 'react'
import { Mic, Zap } from 'lucide-react'
import { AttachmentStub } from '../../attachments'

// The kickoff message's presentation metadata carries only the skill name, so
// the meeting name is recovered from the backend-composed prompt text itself:
// the `<CALENDAR_CONTEXT>` block's `## Meeting Name:` line (http-gateway's
// calendar-context.ts), falling back to the `<AUDIO_NOTE><title>` block when
// the run had no calendar event.
const CALENDAR_MEETING_NAME_PATTERN = /^## Meeting Name: (.+)$/m
const AUDIO_NOTE_TITLE_PATTERN = /<AUDIO_NOTE[^>]*>\s*<title>\s*([^\n<]+)/

export function extractMeetingName(content: string | undefined): string | undefined {
  if (!content) return undefined
  const name = (CALENDAR_MEETING_NAME_PATTERN.exec(content)?.[1] ?? AUDIO_NOTE_TITLE_PATTERN.exec(content)?.[1])?.trim()
  return name && name !== 'Untitled Event' ? name : undefined
}

/**
 * Attachment-pill rendering of the Audio Note Planner kickoff message: the
 * meeting and skill the planner spawned this chat for, shown in place of the
 * autonomous kickoff prompt.
 */
export const PlannerKickoffAttachments = memo(({ content, skill }: { content?: string; skill?: string }) => {
  const meetingName = useMemo(() => extractMeetingName(content), [content])
  return (
    <div className="flex flex-col items-end gap-1.5">
      <div className="flex flex-wrap items-center justify-end gap-1">
        <AttachmentStub label={meetingName ?? 'Audio Note'} icon={<Mic size={16} />} className="max-w-56" />
        {skill && <AttachmentStub label={skill} icon={<Zap size={16} />} className="max-w-56" />}
      </div>
      <div className="bg-element text-foreground-placeholder rounded-xl rounded-br-none border px-4 py-2 text-[13px] italic">
        Highlight detected a helpful {skill ? `${skill} ` : ''}action
      </div>
    </div>
  )
})
PlannerKickoffAttachments.displayName = 'PlannerKickoffAttachments'
